import { useEffect } from 'react';
import { Navigate, Outlet, RouteObject, useLocation, useParams } from 'react-router-dom';

import { LearnerGuard, useRedirectDefaultTeamSlug } from 'redirects';
import { useLocalStorage } from 'support/react/hooks';
import { UserSegment } from 'types/graphql';

import { ProtectedRoute, ProtectedUserSegmentRoute } from './atoms/authorization/ProtectedRoute';
import { inviteRoutes } from './pages/track-invite/InviteRoutes';

export const createAppRoutes: RouteObject[] = [
  {
    path: 'manage/:teamSlug/*',
    lazy: () =>
      import('core/templates/NotificationsContainer').then(({ NotificationsContainer }) => ({
        element: (
          <LearnerGuard>
            <DefaultTeamSlugRedirect />
            <NotificationsContainer />
          </LearnerGuard>
        ),
      })),
    children: [
      {
        // Not part of MainTemplate layout
        path: 'tracks/:trackSlug/dashboard/scripts',
        lazy: () =>
          import('./pages/update-scripts-page/UpdateScriptsPage').then(m => ({ Component: m.UpdateScriptsPage })),
      },
      {
        // Not part of MainTemplate layout
        path: 'invites/:inviteID/live-event/:userID/inspect',
        lazy: () =>
          import('./pages/track-invite/InspectPage').then(({ InspectPage }) => ({
            element: (
              <ProtectedRoute action="read_track_invites:team">
                <InspectPage />
              </ProtectedRoute>
            ),
          })),
      },
      {
        path: '*',
        lazy: () =>
          import('./templates/main-template/MainTemplate').then(({ MainTemplate }) => ({
            element: (
              <MainTemplate>
                <Outlet />
              </MainTemplate>
            ),
          })),
        children: [
          {
            path: 'tracks/:trackSlug/dashboard/*',
            element: (
              <ProtectedRoute action="update:track">
                <main>
                  <Outlet />
                </main>
              </ProtectedRoute>
            ),
            children: [
              {
                index: true,
                lazy: () =>
                  import('./pages/track-dashboard/TrackDashboard').then(m => ({ Component: m.TrackDashboard })),
              },
              {
                path: 'sandbox',
                lazy: () => import('./pages/track-sandbox/TrackSandbox').then(m => ({ Component: m.TrackSandbox })),
              },
              {
                path: 'statistics',
                lazy: () =>
                  import('./pages/track-dashboard-statistics/TrackDashboardStatistics').then(m => ({
                    Component: m.TrackDashboardStatistics,
                  })),
              },
              {
                path: 'happiness',
                lazy: () =>
                  import('./pages/track-dashboard-happiness/TrackDashboardHappiness').then(m => ({
                    Component: m.TrackDashboardHappiness,
                  })),
              },
              {
                path: 'logs',
                lazy: () =>
                  import('./pages/track-dashboard-logs/TrackDashboardLogs').then(m => ({
                    Component: m.TrackDashboardLogs,
                  })),
              },

              {
                path: 'challenges/:challengeSlug/*',
                lazy: () =>
                  import('./templates/challenge-template/ChallengeTemplate').then(m => ({
                    Component: m.ChallengeTemplate,
                  })),
                children: [
                  {
                    path: 'details',
                    lazy: () =>
                      import('./pages/challenge-details-page/ChallengeDetailsPage').then(m => ({
                        Component: m.ChallengeDetailsPage,
                      })),
                  },
                  {
                    path: 'tabs',
                    lazy: () =>
                      import('./pages/challenge-tabs-page/ChallengeTabsPage').then(m => ({
                        Component: m.ChallengeTabsPage,
                      })),
                  },
                  {
                    path: 'quiz',
                    lazy: () =>
                      import('./pages/challenge-quiz-page/ChallengeQuizPage').then(m => ({
                        Component: m.ChallengeQuizPage,
                      })),
                  },
                  {
                    path: 'assignment',
                    lazy: () =>
                      import('./pages/challenge-assignment-page/ChallengeAssignmentPage').then(m => ({
                        Component: m.ChallengeAssignmentPage,
                      })),
                  },
                  {
                    path: 'notes',
                    lazy: () =>
                      import('./pages/challenge-notes-page/ChallengeNotesPage').then(m => ({
                        Component: m.ChallengeNotesPage,
                      })),
                  },
                  {
                    path: 'scripts',
                    lazy: () =>
                      import('./pages/challenge-scripts-page/ChallengeScriptsPage').then(m => ({
                        Component: m.ChallengeScriptsPage,
                      })),
                  },
                  { path: '*', element: <Navigate to="./details" replace /> },
                ],
              },

              { path: '*', element: <Navigate to="." replace /> },
            ],
          },
          {
            path: '*',
            element: (
              <main className="pb-5 md:px-5 md:pt-5">
                <Outlet />
              </main>
            ),
            children: [
              {
                index: true,
                lazy: () =>
                  import('./pages/team-page/TeamPage').then(({ TeamPage }) => ({
                    element: (
                      <ProtectedRoute action="read:team">
                        <TeamPage />
                      </ProtectedRoute>
                    ),
                  })),
              },
              {
                path: 'members',
                lazy: () =>
                  import('./pages/members/ManageTeamMembers').then(({ ManageTeamMembers }) => ({
                    element: (
                      <ProtectedRoute action="read:team">
                        <ManageTeamMembers />
                      </ProtectedRoute>
                    ),
                  })),
              },
              {
                path: 'statistics',
                lazy: () =>
                  import('./pages/team-statistics-page/TeamStatisticsPage').then(({ TeamStatisticsPage }) => ({
                    element: (
                      <ProtectedRoute action="read_statistics:team">
                        <TeamStatisticsPage />
                      </ProtectedRoute>
                    ),
                  })),
              },

              ...inviteRoutes,

              {
                path: 'limits',
                lazy: () =>
                  import('./pages/track-limits-page/TrackLimitsPage').then(({ TrackLimitsPage }) => ({
                    element: (
                      <ProtectedRoute action="update_play_limit:track">
                        <TrackLimitsPage />
                      </ProtectedRoute>
                    ),
                  })),
              },
              {
                path: 'api-keys',
                lazy: () =>
                  import('./pages/api-keys/ApiKeys').then(({ ApiKeys }) => ({
                    element: (
                      <ProtectedRoute action="read_api_key:team">
                        <ApiKeys />
                      </ProtectedRoute>
                    ),
                  })),
              },
              {
                path: 'webhooks',
                lazy: () =>
                  import('./pages/webhooks/Webhooks').then(({ Webhooks }) => ({
                    element: (
                      <ProtectedRoute action="webhook_admin:team">
                        <Webhooks />
                      </ProtectedRoute>
                    ),
                  })),
              },
              {
                path: 'sso',
                lazy: () =>
                  import('./pages/sso-management/SSOManagement').then(({ SSOManagement }) => ({
                    element: (
                      <ProtectedRoute action="read_sso_connection:team">
                        <SSOManagement />
                      </ProtectedRoute>
                    ),
                  })),
              },
              {
                path: 'hot-start/create',
                lazy: () =>
                  import('./pages/manage-hot-start-page/ManageHotStartPage').then(({ ManageHotStartPage }) => ({
                    element: (
                      <ProtectedRoute action="create_hotstart_pool:team">
                        <ManageHotStartPage />
                      </ProtectedRoute>
                    ),
                  })),
              },
              {
                path: 'hot-start/:poolId/edit',
                lazy: () =>
                  import('./pages/manage-hot-start-page/ManageHotStartPage').then(({ ManageHotStartPage }) => ({
                    element: (
                      <ProtectedRoute action="update_hotstart_pool:team">
                        <ManageHotStartPage />
                      </ProtectedRoute>
                    ),
                  })),
              },
              {
                path: 'hot-start/:poolId/details',
                lazy: () =>
                  import('./pages/hot-start-pool-details/HotStartPoolDetailsPage').then(
                    ({ HotStartPoolDetailsPage }) => ({
                      element: (
                        <ProtectedRoute action="read_hotstart_pools:team">
                          <HotStartPoolDetailsPage />
                        </ProtectedRoute>
                      ),
                    }),
                  ),
              },
              {
                path: 'hot-start',
                lazy: () =>
                  import('./pages/hot-start/HotStartPage').then(({ HotStartPage }) => ({
                    element: (
                      <ProtectedRoute action="read_hotstart_pools:team">
                        <HotStartPage />
                      </ProtectedRoute>
                    ),
                  })),
              },
              {
                path: 'secrets',
                lazy: () =>
                  import('./pages/team-secrets/TeamSecrets').then(({ TeamSecretsContainer }) => ({
                    element: (
                      <ProtectedRoute action="read_secrets:team">
                        <TeamSecretsContainer />
                      </ProtectedRoute>
                    ),
                  })),
              },
              {
                path: 'sandboxes',
                lazy: () =>
                  import('./pages/sandboxes/Sandboxes').then(({ SandboxesPage }) => ({
                    element: (
                      <ProtectedRoute action="list:sandbox">
                        <SandboxesPage />
                      </ProtectedRoute>
                    ),
                  })),
              },
              {
                path: 'arcades',
                lazy: () =>
                  import('./pages/arcades/ArcadeListPage').then(({ ArcadeListPage }) => ({
                    element: (
                      <ProtectedRoute action="read_arcades:team">
                        <ArcadeListPage />
                      </ProtectedRoute>
                    ),
                  })),
              },
              {
                path: 'arcades/:arcadeID',
                lazy: () =>
                  import('./pages/arcades/ArcadeEditPage').then(({ ArcadeEditPage }) => ({
                    element: (
                      <ProtectedRoute action="read_arcades:team">
                        <ArcadeEditPage />
                      </ProtectedRoute>
                    ),
                  })),
              },
              {
                path: 'sandbox-presets/add',
                lazy: () =>
                  import('./pages/manage-sandbox-preset-page/ManageSandboxPresetPage').then(
                    ({ ManageSandboxPresetPage }) => ({
                      element: (
                        <ProtectedRoute action="update:sandbox-config">
                          <ManageSandboxPresetPage />
                        </ProtectedRoute>
                      ),
                    }),
                  ),
              },
              {
                path: 'sandbox-presets/preview/:configID',
                lazy: () =>
                  import('./pages/preview-sandbox-preset/PreviewSandboxPresetPage').then(
                    ({ PreviewSandboxPresetPage }) => ({
                      element: (
                        <ProtectedRoute action="read:sandbox-config">
                          <PreviewSandboxPresetPage />
                        </ProtectedRoute>
                      ),
                    }),
                  ),
              },
              {
                path: 'sandbox-presets/edit/:configID',
                lazy: () =>
                  import('./pages/manage-sandbox-preset-page/ManageSandboxPresetPage').then(
                    ({ ManageSandboxPresetPage }) => ({
                      element: (
                        <ProtectedRoute action="update:sandbox-config">
                          <ManageSandboxPresetPage />
                        </ProtectedRoute>
                      ),
                    }),
                  ),
              },
              {
                path: 'sandbox-presets',
                lazy: () =>
                  import('./pages/sandbox-presets-list-page/SandboxPresetsListPage').then(
                    ({ SandboxPresetsListPage }) => ({
                      element: (
                        <ProtectedRoute action="read:sandbox-config">
                          <SandboxPresetsListPage />
                        </ProtectedRoute>
                      ),
                    }),
                  ),
              },
              {
                path: 'inspiration-library',
                lazy: () =>
                  import('./pages/inspiration-library-page/InspirationLibraryPage').then(
                    ({ InspirationLibraryPage }) => ({
                      element: (
                        <ProtectedUserSegmentRoute segment={UserSegment.ContentCreator}>
                          <InspirationLibraryPage />
                        </ProtectedUserSegmentRoute>
                      ),
                    }),
                  ),
              },
              {
                path: 'plays-report',
                lazy: () =>
                  import('./pages/play-activity-report/PlayActivityReport').then(({ PlayActivityReport }) => ({
                    element: (
                      <ProtectedRoute action="read_statistics:team">
                        <PlayActivityReport />
                      </ProtectedRoute>
                    ),
                  })),
              },
              {
                path: 'users-report',
                lazy: () =>
                  import('./pages/user-report/UserReport').then(({ UserReport }) => ({
                    element: (
                      <ProtectedRoute action="read_statistics:team">
                        <UserReport />
                      </ProtectedRoute>
                    ),
                  })),
              },
              {
                path: 'insights',
                lazy: () =>
                  import('./pages/insights/Insights').then(({ Insights }) => ({
                    element: (
                      <ProtectedRoute action="read_statistics:team">
                        <Insights />
                      </ProtectedRoute>
                    ),
                  })),
              },
              {
                path: 'tags',
                lazy: () =>
                  import('./pages/manage-tags-page/ManageTagsPage').then(({ ManageTagsPage }) => ({
                    element: (
                      <ProtectedRoute action="create:track">
                        <ManageTagsPage />
                      </ProtectedRoute>
                    ),
                  })),
              },
              {
                path: 'custom-resources/*',
                element: (
                  // TODO: fix on new permisision implemented
                  <ProtectedRoute action="create:track">
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [
                  {
                    index: true,
                    lazy: () =>
                      import('./pages/custom-resources-list-page/CustomResourcesListPage').then(
                        ({ CustomResourcesListPage }) => ({
                          element: <CustomResourcesListPage />,
                        }),
                      ),
                  },
                  {
                    path: 'registry',
                    lazy: () =>
                      import('./pages/terraform-registry-page/TerraformRegistryPage').then(
                        ({ TerraformRegistryPage }) => ({
                          element: <TerraformRegistryPage />,
                        }),
                      ),
                  },
                  {
                    path: '*',
                    element: <Navigate to="." replace />,
                  },
                ],
              },

              { path: '*', element: <Navigate to="./" replace /> },
            ],
          },
        ],
      },
    ],
  },
];

const DEFAULT_TEAM_PLACEHOLDER = '$';

function DefaultTeamSlugRedirect() {
  const { teamSlug = '' } = useParams<'teamSlug'>();
  const { defaultTeamSlug } = useRedirectDefaultTeamSlug();
  const { pathname, search, hash, state } = useLocation();
  const [, setStorageSlug] = useLocalStorage<string>('team');

  useEffect(() => {
    if (teamSlug !== DEFAULT_TEAM_PLACEHOLDER) setStorageSlug(teamSlug);
  }, [teamSlug, setStorageSlug]);

  if (teamSlug === DEFAULT_TEAM_PLACEHOLDER) {
    return (
      <Navigate
        to={{
          pathname: pathname.replace(`manage/${DEFAULT_TEAM_PLACEHOLDER}`, `manage/${defaultTeamSlug}`),
          hash,
          search,
        }}
        state={state}
        replace
      />
    );
  }

  return <Outlet />;
}
